import {booleanAttribute, ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, HostBinding, Injector, Input, input, OnDestroy, OnInit, Optional, output, Signal, signal, viewChild, ViewContainerRef} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FormControl, FormControlDirective, FormsModule, NgControl, ReactiveFormsModule, Validators} from "@angular/forms";
import {combineLatestWith, debounce, debounceTime, firstValueFrom, fromEvent, merge, Observable, of, Subject, timer} from "rxjs";
import {MatIcon} from "@angular/material/icon";
import {MatButton, MatIconButton} from "@angular/material/button";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {erIkkeTomStreng, erTomEllerNil, trimTilNull} from "felles-ng2/støttefunksjoner/strenger";
import {NgxColorsModule, NgxColorsTriggerDirective} from "ngx-colors";
import {TranslateModule} from "@ngx-translate/core";
import {RiktekstRedigeringTabellstørrelseVelgerKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/komponenter/tabellstørrelse-velger/tabellstørrelse-velger";
import {NedtrekksmenyKomponent} from "web-ng2/delt/komponenter/nedtrekksmeny/nedtrekksmeny";
import {KonfigurasjonsCallback, NedtrekksmenyTriggerForDirektiv} from "web-ng2/delt/komponenter/nedtrekksmeny/nedtrekksmeny-trigger-for";
import {GruppeId, HtmlTekst} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {EksternEllerInternLenkeSpesifikasjon} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/komponenter/lenke-rediger/lenke-rediger";
import {MAT_FORM_FIELD, MatFormField, MatFormFieldControl} from "@angular/material/form-field";
import {BooleanInput} from "@angular/cdk/coercion";
import {BubbleMenuPluginProps} from "@tiptap/extension-bubble-menu";
import {TipTapEditor, TipTapEditor_DI_TOKEN} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/TipTapEditor";
import {Overlay} from "@angular/cdk/overlay";
import {TiptapBubbleMenuDirective} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/ngx-tiptap/src/lib/bubble-menu.directive";
import {TiptapEditorDirective} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/ngx-tiptap/src/lib/editor.directive";
import {RiktekstRedigeringOverskriftVelgerKomponentKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/komponenter/overskrift-velger/overskrift-velger";
import {RiktekstRedigeringFontStørrelseVelgerKomponentKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/komponenter/font-størrelse-velger/font-størrelse-velger";
import {RiktekstRedigeringFontTypeVelgerKomponentKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/komponenter/font-type-velger/font-type-velger";
import {finnNodensNærmesteForelderAvType} from "felles-ng2/støttefunksjoner/dom";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {delay, map} from "rxjs/operators";
import {nil} from "felles-ng2/typer/nullish";
import {Lenkepanel} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/komponenter/lenke-rediger/Lenkepanel";
import {MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltip, MatTooltipDefaultOptions} from "@angular/material/tooltip";
import {NgTemplateOutlet} from "@angular/common";
import {nårGuiErOppdatert} from "felles-ng2/infrastruktur/angular/nårGuiErOppdatert";
import {InterneLenker} from "felles-ng2/tjenester/InterneLenker";
import {DialogParametre, RiktekstRedigeringFullskjermmodalKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/komponenter/riktekst-redigering-fullskjermmodal/riktekst-redigering-fullskjermmodal";
import {MatDialog} from "@angular/material/dialog";
import {Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";


const debug = initDebugger('RiktekstRedigeringKomponent');

type Tekstmarkering = {
    type: 'text';
    anchor: number;
    head: number;
}

type ShouldShowFunksjon = Exclude<BubbleMenuPluginProps['shouldShow'], null>;

/**
 * Komponent for redigering av rik tekst.
 * Benytter Tiptap som underliggende rik-tekst-redigeringsrammeverk, se https://tiptap.dev.
 * Merk at ngx-tiptap (angular-bindingen for tiptap) > ver 11 krever angular 19, kan derfor ikke oppgradere til nyeste
 * ver av ngx-tiptap før vi er på angular 19.
 *
 * https://taiga-family.github.io/editor/starter-kit
 * https://github.com/taiga-family/editor
 *
 * Se https://github.com/umodoc/editor for en vue3/tiptap basert dokument editor med mye funksjonalitet
 *
 * https://github.com/sjdemartini/mui-tiptap react/material basert som har en 'flytende meny for tabell' variant
 */
@Component({
    selector: 'riktekst-redigering',
    templateUrl: 'riktekst-redigering.html',
    styleUrl: 'riktekst-redigering.scss',
    host: {
        '[class.fokus]': 'focusedSomSignal()',
        '[class.menylinje-kun-ved-aktiv]': 'menylinjeKunVedAktiv()',
        '[class.uten-menylinje]': 'skjulMenylinje()',
        '[class.menylinje-over-flere-rader]': 'formateringsvalgmenyBrekkerOverFlereLinjer()',
        '[style.--redigeringsfelt-maks-bredde-ved-pdf]': 'maksPdfBredde+"px"'
    },
    providers: [
        {provide: MatFormFieldControl, useExisting: RiktekstRedigeringKomponent},
        /** mat-select ser etter MAT_FORM_FIELD og vil dersom denne er satt til ikke-null bla plassere panelet med nedtrekkslisten i underkant av injisert mat-form-field,
         * noe som blir helt feil i vårt tilfelle da riktekst-redigering er mye høyere enn kun én linje. Ulempen er at  */
        {provide: MAT_FORM_FIELD, useValue: null},
        {provide: TipTapEditor_DI_TOKEN, useFactory: (komponent: RiktekstRedigeringKomponent) => komponent.tiptapEditor, deps: [RiktekstRedigeringKomponent]},
        /** standard valg for alle tooltips (påvirker også tooltip i bla lenke-rediger komp), setter delay slik at ikke tooltip maskerer knapper i formateringsrad 1 når mus beveges fra editor over rad 2 til rad 1 */
        {provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: {position: 'above', showDelay: 500} as MatTooltipDefaultOptions},
    ],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatIcon,
        NgxColorsModule,
        TranslateModule,
        RiktekstRedigeringTabellstørrelseVelgerKomponent,
        NedtrekksmenyKomponent,
        NedtrekksmenyTriggerForDirektiv,
        TiptapBubbleMenuDirective,
        TiptapEditorDirective,
        RiktekstRedigeringOverskriftVelgerKomponentKomponent,
        RiktekstRedigeringFontStørrelseVelgerKomponentKomponent,
        RiktekstRedigeringFontTypeVelgerKomponentKomponent,
        MatIconButton,
        MatTooltip,
        NgTemplateOutlet,
        MatButton,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RiktekstRedigeringKomponent implements OnInit, OnDestroy, MatFormFieldControl<HtmlTekst> {

    /** nødvendig plass for at formateringsvalg i <riktekst-redigering>-elementet som alltid vises ikke skal brekke over flere linjer når <riktekst-redigering> ligger i et <mat-form-field> element.
     * Denne MÅ justeres dersom innholdet i formateringsvalg endres.
     * MERK må holdes i synk med $mat-form-field-riktekst-redigering-min-bredde i riktekst-redigering-mixins.scss */
    public static readonly mat_form_field_riktekst_redigering_min_bredde_px = 500;

    tipTapEditorFormControl = viewChild(FormControlDirective);
    tekstfargevelgerDirektiv = viewChild('tekstfargevelger', {read: NgxColorsTriggerDirective});
    bakgrunnsfargevelgerDirektiv = viewChild('bakgrunnsfargevelger', {read: NgxColorsTriggerDirective});
    /** undefined dersom menylinjen ikke tegnes ut, se {@link #skjulMenylinje} */
    formateringsvalg = viewChild<ElementRef<HTMLElement>>('formateringsvalg');

    /** Signalisere at fullskjermvisning skal avsluttes, brukes ifbm {@link RiktekstRedigeringFullskjermmodalKomponent} */
    vedLukkFullskjermvisning = output();

    /** Skjemakontrolleren som html-innholdet i editoren er knyttet til. */
    skjemakontroll = input.required<FormControl<HtmlTekst | null>>();
    /** Gruppeid som søk etter entitet begrenses til ved opprettelse av internlenke. Dersom null vil ikke opprettelse av internlenke være tilgjengelig. */
    gruppeId = input<GruppeId | nil>(null);
    /** viser menylinje kun når redigeringsfelt er aktivt, med mindre satt til false (true som standard) */
    menylinjeKunVedAktiv = input(true, {transform: booleanAttribute});
    /** dersom produsert html skal brukes i generert PDF kan denne settes til true,
     * vil da gjøre 'sett inn sideskift' formateringsvalget tilgjengelig og begrense bredden på redigeringsfeltet til 630px (som er ca tilgjengelig horisontal plass i genererte PDFer) */
    innholdBrukesIPdf = input(false, {transform: booleanAttribute});
    /** Som standard viser vi kun de mest sentrale formateringsvalgene (resten vises når bruker klikker på '...' i formateringsraden), dersom satt til true vises alltid alle valgene */
    visAlleFormateringsvalg = input(false, {transform: booleanAttribute});
    /** Styrer hvorvidt knapp for åpne- eller lukk-fullskjermvisning vises, kun eksponert for bruk i {@link RiktekstRedigeringFullskjermmodalKomponent} */
    erFullskjermvisning = input(false, {transform: booleanAttribute});
    /** dersom satt til true vises ikke menylinjen med formateringsvalg, kontekstmeny ved tekstmarkering vises fortsatt (false som standard) */
    skjulMenylinje = input(false, {transform: booleanAttribute});

    focusedSomSignal = signal(false);
    formateringsverktoeyErAktivt = signal(false);
    /** maksimal bredde på redigeringsfeltet når innholdet skal vises i PDF */
    maksPdfBredde = 630;
    redigeringsfeltErBredereEnnMaksPdfBredde: Signal<boolean>;
    /** sann dersom den ekspanderte formateringsmenyen brekker over flere linjer, returnerer altid false dersom ikke {@link #viserAlleFormateringsvalg()} */
    formateringsvalgmenyBrekkerOverFlereLinjer: Signal<boolean>;
    /** sann dersom alle formateringsvalg skal vises, styres via knapp for dette i formateringsvalg eller ved at attributtet 'visAlleFormateringsvalg' er satt til true */
    viserAlleFormateringsvalg = signal(false);
    tekstfargeKontroll = new FormControl<string | undefined>(undefined);
    bakgrunnsfargeKontroll = new FormControl<string | undefined>(undefined);
    nyTabellMenyKonfigurasjon: KonfigurasjonsCallback = (overlay, elementRef) => this.konfigurerNyTabellMeny(overlay, elementRef);
    tiptapEditor: TipTapEditor;

    private lenkepanel = new Lenkepanel(this.overlay, this.viewContainerRef);
    private åpenFullskjermvisningDialog: ReturnType<typeof RiktekstRedigeringFullskjermmodalKomponent.vis> | null = null;

    constructor(private el: ElementRef<HTMLElement>,
                private destroyRef: DestroyRef,
                private oversettelser: Oversettelser,
                interneLenker: InterneLenker,
                private injector: Injector,
                private viewContainerRef: ViewContainerRef,
                private overlay: Overlay,
                private dialog: MatDialog,
                @Optional() private omsluttendeMatFormField: MatFormField | null) {

        this.tiptapEditor = new TipTapEditor(interneLenker, this.injector);

        const vedVinduResize = fromEvent(window, 'resize').pipe(debounceTime(1000));
        this.redigeringsfeltErBredereEnnMaksPdfBredde = toSignal(merge(vedVinduResize, toObservable(this.tiptapEditor.harFokus))
          .pipe(map(() => this.el.nativeElement.getBoundingClientRect().width > this.maksPdfBredde)), {initialValue: false});
        this.formateringsvalgmenyBrekkerOverFlereLinjer = toSignal(merge(vedVinduResize, toObservable(this.viserAlleFormateringsvalg))
          .pipe(
            delay(100), //vent til formateringsvalgene har blitt tegnet ut - eller blir høydeberegningen feil
            map(() => this.beregnFormateringsvalgmenyBrekkerOverFlereLinjer())), {initialValue: false});

        //sørger for å reflektere at tiptapeditor har fokus i focusedSomSignal
        toObservable(this.tiptapEditor.harFokus)
          .pipe(
            //ved klikk i formateringsmenyen mister tiptap midlertidig fokus, kompenserer for dette her ved å
            //ikke indikere at fokus er mistet før det har gått noe tid, dermed får ikke disse raske fokus->ikke-fokus(pga klikk i meny)->fokus effekt
            debounce(harFokus => !harFokus ? timer(500).pipe(map(() => false)) : of(harFokus)),
            //ta hensyn til at lenkepanel eller formateringsverktøy som åpner paneler er åpnet
            combineLatestWith(this.lenkepanel.vises, toObservable(this.formateringsverktoeyErAktivt)),
            map(([harFokus, lenkepanelÅpnet, formateringsverktoeyErAktivt]) => harFokus || lenkepanelÅpnet || formateringsverktoeyErAktivt),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe(harFokus => {
              this.focusedSomSignal.set(harFokus);
              //signaliser til mat-form-field at tilstanden vår er endret (i det tilfellet fokus-tilstanden)
              this.stateChanges.next();
              //sørg for at eventuell påstartet ny lenke avbrytes
          });

        //aktiverer visning av alle formateringsvalg dersom visAlleFormateringsvalg-attributtet er satt til true
        toObservable(this.visAlleFormateringsvalg).subscribe(visAlle => this.viserAlleFormateringsvalg.set(visAlle));

        this.initTekstfargeKontroll();
        this.initBakgrunnsfargeKontroll();
        this.visLenkepanelVedAktivLenke();

    }

    giTekstfeltFokus() {
        this.tiptapEditor.giFokus();
    }

    protected endreVisAlleFormateringsvalg() {
        this.viserAlleFormateringsvalg.update(vises => !vises);
        this.giTekstfeltFokus();
    }

    protected async aapneIFullskjermvisning() {
        this.åpenFullskjermvisningDialog = RiktekstRedigeringFullskjermmodalKomponent.vis(this.dialog, this.fullskjermModalParametre(), this.oversettelser);

        // vent til dialog har blitt lukket
        await firstValueFrom(this.åpenFullskjermvisningDialog.beforeClosed());

        // sørg for å oppdatere editor med potensielt endret innhold som dialog har skrevet til skjemakontroll
        // dette må gjøres da tiptap-editor ikke lytter til endringer i angitt skjemakontroll etter at komponenten er initialisert,
        // noe som ikke er unaturlig da dette ville ført til en endring->oppdatering->endring loop
        this.tiptapEditor.editor.chain().focus().setContent(this.skjemakontroll().value, false).run();
    }

    protected lukkFullskjermvisning() {
        this.vedLukkFullskjermvisning.emit();
    }

    /** Returnerer sann dersom bruker har markert tekst og tekstformateringsmeny skal vises inline */
    protected readonly skalKontekstmenyTekstformateringVises: ShouldShowFunksjon = (props) => {
        return this.tiptapEditor.erTekstMarkert() && !this.tiptapEditor.erLinkTag();
    };

    protected formateringsverktoeyAktivert(aktivert: boolean) {
        this.formateringsverktoeyErAktivt.set(aktivert);
        if (!aktivert && !this.tiptapEditor.harFokus()) this.giTekstfeltFokus();
    }

    protected tekstfargevelgerAktivert() {
        this.formateringsverktoeyAktivert(true);
        this.gjenopprettTekstmarkeringVedKlikkeIFargevelger(this.tekstfargevelgerDirektiv()!);
    }

    protected bakgrunnsfargevelgerAktivert() {
        this.formateringsverktoeyAktivert(true);
        this.gjenopprettTekstmarkeringVedKlikkeIFargevelger(this.bakgrunnsfargevelgerDirektiv()!);
    }

    /** Gir fokus til tiptap-editor felt, med mindre feltet allerede har fokus */
    protected giEditorFokus(event: Event) {
        if ((event.target as Element).tagName.toLowerCase() == 'input') return; //trenger ikke å sette fokus dersom klikket var i inputfeltet
        if (!this.tiptapEditor.harFokus()) this.giTekstfeltFokus();
    }

    /** Sørger for at paste-event ikke bobler opp.
     * Dermed vil ikke paste-handler registrert av ngf-drop i BasisKontroller laste opp filer som måtte være i de pastede dataene */
    protected stoppPropageringAvPasteEvent(event: Event) {
        event.stopPropagation();
    }

    /** Returnerer sann dersom bruker har markert tekst og tekstformateringsmeny skal vises inline */

    //ng2 livssykelmetoder

    ngOnInit(): void {
        //sørger for å knyttet label-elementet i mat-form-field (dersom mat-label er angitt) til det redigerbare div-elementet i tiptapeditor
        if (erIkkeNil(this.omsluttendeMatFormField?.getLabelId())) this.tiptapEditor.setLabeledById(this.omsluttendeMatFormField.getLabelId()!)

        this.skjemakontroll().valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.stateChanges.next());
    }

    ngOnDestroy() {
        debug('ngOnDestroy');
        this.åpenFullskjermvisningDialog?.close();
        this.stateChanges.complete();
        this.tiptapEditor.terminer();
    }

    // private hjelpemetoder

    /** Tekstverdien til tilknyttet mat-label element, null dersom slikt ikke finnes eller inneholder blank tekst */
    private matFormFieldLabel(): string | null {
        if (erNil(this.omsluttendeMatFormField?.getLabelId())) return null;
        return trimTilNull(document.getElementById(this.omsluttendeMatFormField.getLabelId()!)?.innerText);
    }

    private fullskjermModalParametre(): DialogParametre {
        return {
            skjemakontroll: this.skjemakontroll(),
            gruppeId: this.gruppeId(),
            innholdBrukesIPdf: this.innholdBrukesIPdf(),
            tittel: this.matFormFieldLabel()
        }
    }

    private konfigurerNyTabellMeny(overlay: Overlay, elementRef: ElementRef<HTMLElement>) {
        return {
            positionStrategy: overlay
              .position()
              .flexibleConnectedTo(elementRef)
              .withGrowAfterOpen(true)
              //plassert øvre venstre hjørne av menyen på nedre venstre hjørne av trigger-knapp
              .withPositions([{originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top'}])
        }
    }

    private initBakgrunnsfargeKontroll() {
        this.bindTilKontroll(this.bakgrunnsfargeKontroll,
          farge => this.tiptapEditor.setBakgrunnsfarge(farge),
          () => this.tiptapEditor.lesBakgrunnsfarge())
    }


    private initTekstfargeKontroll() {
        this.bindTilKontroll(this.tekstfargeKontroll,
          farge => this.tiptapEditor.setTekstfarge(farge),
          () => this.tiptapEditor.lesTekstfarge())
    }

    private bindTilKontroll<T>(feltKontroll: FormControl<T>, oppdaterEditor: (verdi: T) => void, gjeldendeVerdi: () => T) {
        this.lyttTilEndring(feltKontroll, oppdaterEditor)
        this.vedTilstandEndret(gjeldendeVerdi).subscribe(verdi => feltKontroll.setValue(verdi, {emitEvent: false}))
    }

    /**
     * Klikk i fargevelger fører til at markert tekst mister sin synlige markering i nettleseren, sørger her for at markeringen gjenopprettes ved klikk .
     *  panelet med fargevelger-gui er ikke rendret enda når fargevelgerAktivert() kalles, venter derfor til etter neste rendring
     * */
    private gjenopprettTekstmarkeringVedKlikkeIFargevelger(fargevelger: NgxColorsTriggerDirective) {
        //panelet med fargevelger-gui er ikke rendret enda når NgxColorsTriggerDirective.open emitter, venter derfor til etter neste rendring
        nårGuiErOppdatert(() =>
            fargevelger.panelRef!.instance!.panelRef.nativeElement.addEventListener('click', (event: MouseEvent) => this.giTekstfeltFokus()),
          this.injector);
    }

    private vedTilstandEndret<T>(mapFunksjon: () => T): Observable<T> {
        return this.tiptapEditor.vedTilstandEndret(mapFunksjon);
    }

    private lyttTilEndring<T>(feltKontroll: FormControl<T>, vedEndring: (verdi: T) => void) {
        feltKontroll.valueChanges
          .pipe(takeUntilDestroyed())
          .subscribe(vedEndring);
    }

    private beregnFormateringsvalgmenyBrekkerOverFlereLinjer() {
        const formateringsvalg = this.formateringsvalg()?.nativeElement;
        //menylinjen er ikke tegnet ut (skjulMenylinje er satt) - kan da ikke brekke over flere linjer
        if (erNil(formateringsvalg)) return false;
        //finn høyden til formateringsvalg-elementet når kun én rad vises, gjør dette ved å ta høyden til det første .gruppe elementet i denne
        const radHøyde = formateringsvalg.querySelector('.gruppe')?.getBoundingClientRect().height ?? 0;
        //høyden til hele formateringsvalg-elementet, som kan være mer enn kun én rad når visAlleFormateringsvalg() = true
        const elementHøyde = formateringsvalg.getBoundingClientRect().height;
        return (elementHøyde - radHøyde - 10 /* .formateringsvalg kan ha padding og ramme */) > 0;
    }

    //håndtering av lenke-redigering

    /** Viser panel med gui for å vise/redigere lenke når bruker har plassert markør i en a-tag */
    private visLenkepanelVedAktivLenke() {
        let forrigeAnkerelement: null | HTMLElement = null;
        effect(() => {
            const lenkeinfo = this.tiptapEditor.aktivLenke();
            const ankerelement = erIkkeNil(lenkeinfo) ? this.lenkensAnkerelement() : null;
            const erLenkeUendret = ankerelement === forrigeAnkerelement;
            forrigeAnkerelement = ankerelement ?? null;
            //når bruker flytter markør ut av lenke lukker vi lenkepanelet
            if (erNil(lenkeinfo)) return this.lenkepanel.lukk();
            //bruker har flyttet markør innenfor en og samme lenke mens lenkepanel vises - da gjør vi ingen ting
            if (erLenkeUendret && this.lenkepanel.vises.value) return;
            //editor er ikke aktiv - da skal ikke lenkepanel vises
            if (!this.focused) return;

            //markør har blitt flyttet inni en lenke-tag (eller bruker klikket på 'ny lenke'
            this.lenkepanel.visMedRedigerStøtte(lenkeinfo, ankerelement!, this.gruppeId(), {
                vedSlettLenke: () => this.tiptapEditor.editor.chain().focus().slettLenke().run(),
                vedSetAdresse: spesifikasjon => this.oppdaterLenk(spesifikasjon),
                //dersom bruker klikker utenfor redigeringsfeltet må vi sørge for at eventuell som lenke blir fjernet
                //ved flytting av markør internt i redigeringsfelt blir dette håndtert vhja lytting til 'selectionUpdate' event i
                //LenkeKommandoer.lagNyLenkeMedTomHref, men når fokus flyttes ut av redigeringsfeltet blir ikke denne eventet trigget,
                //ergo må vi håndtere dette tilfellet her
                vedLukket: () => this.tiptapEditor.editor.commands.unsetLinkDersomTomHref()
            });
        });
    }

    private lenkensAnkerelement(): HTMLElement {
        let aktivHtmlNode = this.tiptapEditor.editor.view.domAtPos(this.tiptapEditor.editor.state.selection.from).node;
        return finnNodensNærmesteForelderAvType(aktivHtmlNode, 'a') ??
        //når bruker klikker på 'ny lenk' uten at noen tekst er valgt vil det ikke eksistere noe a-tag enda, bruker da nærmeste element-node
        aktivHtmlNode.nodeType == Node.TEXT_NODE ? aktivHtmlNode.parentElement! : aktivHtmlNode as HTMLElement;
    }

    private oppdaterLenk(spesifikasjon: EksternEllerInternLenkeSpesifikasjon): void {
        //dersom ingen tekst var markert når bruker klikket på 'ny lenke' -> aktiveNodePos.mark != link && erLinkTag() == true
        //dersom tekst var markert når bruker klikket på 'ny lenke' -> aktiveNodePos.mark == link && erLinkTag() == true
        const erNyLenkeUtenTekstmarkering = this.tiptapEditor.erLinkTag() && !this.tiptapEditor.harAktivNodeLinkMark();

        if (erNyLenkeUtenTekstmarkering) {
            //dersom Link-mark er aktiv via stored-mark men omkringliggende node ikke har Link-mark satt -> ny lenketag med angitt lenketekst
            //dette vil være tilfelle når bruker klikker på 'ny lenk' og ingen tekst er markert
            this.tiptapEditor.editor.commands.lagNyLenke(spesifikasjon.adresse, spesifikasjon.lenketekst ?? spesifikasjon.adresse, spesifikasjon.attributter);
        } else {
            //eksisterende lenketekst er IKKE tom -> oppdater lenkeadresse
            //dette vil være tilfelle dersom bruker markerer tekst og klikker på 'ny lenke'
            //eller når markør står i eksisterende lenke som har lenketekst og klikker 'rediger lenke'
            if (spesifikasjon.oppdaterLenketekst && erIkkeTomStreng(spesifikasjon.lenketekst))
                this.tiptapEditor.editor.commands.setLenkeadresseOgLenketekst(spesifikasjon.adresse, spesifikasjon.lenketekst, spesifikasjon.attributter);
            else
                this.tiptapEditor.editor.commands.setLenkeadresse(spesifikasjon.adresse, spesifikasjon.attributter);
        }
    }

    //MatFormFieldControl implementasjon

    /** brukes til å lage unike IDer, se {@link #id}*/
    static nesteId = 0;

    private _placeholder: string = '';
    private _required = false;

    @Input('aria-describedby') userAriaDescribedBy!: string;

    get placeholder() {
        return this.shouldLabelFloat ? this._placeholder : '';
    }

    //@todo vises ikke
    @Input() set placeholder(plh) {
        this._placeholder = plh;
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    get required() {
        return this._required ||
          (this.skjemakontroll().hasValidator(Validators.required) ?? false);
    }

    @Input({transform: booleanAttribute}) set required(req: boolean) {
        this._required = req;
        if (req) this.skjemakontroll().addValidators(Validators.required);
        else this.skjemakontroll().removeValidators(Validators.required);
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    get disabled(): boolean {
        return this.skjemakontroll().disabled
    }

    @Input() set disabled(value: BooleanInput) {
        // this.setDisabledState(coerceBooleanProperty(value)) //@todo
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    public get ngControl(): NgControl {
        return this.tipTapEditorFormControl()!;
    }

    //vi ønsker ikke at MatFormField skal sette 'for' attributtet på label elementet da dette da dette kun kan peke til standard form-elementer (input/textarea etc)
    //vi knytter label-feltet til relevant redigerbart div-element ved å sette aria-labelledby attributte på dette elementet i onInit
    public disableAutomaticLabeling = true;

    readonly stateChanges = new Subject<void>();

    @HostBinding() id = `riktekst-redigering-${RiktekstRedigeringKomponent.nesteId++}`;

    controlType = 'riktekst-redigering';


    /** se {@link #vedFokusMottatt} / {@link #vedFokusMistet} */
    get focused() {
        return this.focusedSomSignal();
    }

    get value(): HtmlTekst | null {
        return this.skjemakontroll().value;
    }

    set value(tekst: HtmlTekst | null) {
        this.skjemakontroll().setValue(tekst ?? '');
    }

    get empty() {
        return erTomEllerNil(this.skjemakontroll().value);
    }

    get shouldLabelFloat() {
        //når menylinjen alltid vises (menylinjeKunVedAktiv = false) må label alltid flyte for ikke å bli dekket av menylinjen,
        //uten menylinje flyter label som for vanlige tekstfelt
        return this.focused || !this.empty || (!this.menylinjeKunVedAktiv() && !this.skjulMenylinje());
    }

    get errorState(): boolean {
        return this.skjemakontroll().invalid;
    }

    setDescribedByIds(ids: string[]) {
        this.tiptapEditor.setDescribedByIds(ids);
    }

    onContainerClick(event: MouseEvent) {
        this.giEditorFokus(event);
    }
}

